import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { motion, useScroll } from 'framer-motion';
import { useMedia } from 'react-use';
import Heading from 'components/shared/heading';
import styles from './styles.module.scss';

const MOTION_EASE = [0.43, 0.13, 0.23, 0.96];
const ENTER_OFFSET = 40; // px the active pane rises from (revealed from the bottom)
const ENTER_DURATION = 0.45;
const MOBILE_QUERY = '(max-width: 767.98px)';
// Scroll distance (in vh) to advance one tab on desktop. The pinned track is
// `100vh sticky frame + count * this`, so each tab occupies exactly this much scroll.
// Lower = fewer scrolls per tab (snappier); higher = more deliberate. Was effectively
// 75vh (count*100vh track), which felt like it needed a full screen of scroll per tab.
const SCROLL_VH_PER_TAB = 45;

// Gatsby image markup uses inline styles that differ between SSR and client (the fade-in), so
// recursively mark the subtree to skip React's attribute diffing and avoid hydration warnings.
const withSuppressHydrationWarning = (node) => {
  if (!React.isValidElement(node)) return node;
  const children = React.Children.map(node.props.children, withSuppressHydrationWarning);
  return React.cloneElement(node, { suppressHydrationWarning: true }, children);
};

// Tags arrive from WP as a comma-separated string (Lazy Blocks can't nest a repeater inside the
// tabs repeater). Split on commas; also tolerate an array of strings/objects as a fallback.
const normalizeTags = (value) => {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          return item.value || item.label || item.text || item.tag || '';
        }
        return '';
      })
      .filter(Boolean);
  }
  return [];
};

const PaneContent = ({ tab, imageNode }) => {
  const tags = normalizeTags(tab.tags);
  return (
    <>
      <div className={styles.paneText}>
        {tab.title && (
          <Heading tag="h3" size="lg" className={styles.paneTitle}>
            {tab.title}
          </Heading>
        )}
        {tab.description && (
          <div
            className={styles.paneDescription}
            dangerouslySetInnerHTML={{ __html: tab.description }}
          />
        )}
        {tags.length > 0 && (
          <div className={styles.tagsGroup}>
            <span className={styles.tagsTitle}>{tab.tagsTitle || 'What this replaces'}</span>
            <ul className={styles.tags}>
              {tags.map((value, i) => (
                <li key={i} className={styles.tag}>
                  {value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className={styles.paneMedia}>
        <div className={styles.mediaBox}>{imageNode}</div>
      </div>
    </>
  );
};

const StackCards = ({
  eyebrow = '',
  title = null,
  description = '',
  theme = '',
  tabs = [],
  children = null,
}) => {
  const trackRef = useRef(null);
  const tabsNavRef = useRef(null);
  const isMobile = useMedia(MOBILE_QUERY, false);
  const count = tabs.length;
  const [activeIndex, setActiveIndex] = useState(0);

  // WordPress passes one Gatsby-optimized image per tab as the block's inner content;
  // map them to tabs positionally (image N → tab N).
  // Decorate once (memoized) so we don't re-clone the image subtree on every
  // scroll-driven re-render — only when the children themselves change.
  const mediaNodes = useMemo(
    () =>
      React.Children.toArray(children)
        .filter((child) => React.isValidElement(child))
        .map(withSuppressHydrationWarning),
    [children]
  );
  const mediaFor = (index) => mediaNodes[index] || null;

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start start', 'end end'],
  });

  // Desktop: scrolling the pinned track advances the active tab one-by-one (discrete).
  useEffect(() => {
    if (isMobile || count === 0) return undefined;
    const update = (v) => {
      const idx = Math.min(count - 1, Math.max(0, Math.floor(v * count)));
      setActiveIndex((cur) => (cur === idx ? cur : idx));
    };
    update(scrollYProgress.get());
    const unsubscribe = scrollYProgress.on('change', update);
    return unsubscribe;
  }, [scrollYProgress, count, isMobile]);

  // Clicking a tab is a hard jump: switch instantly and snap the pinned scroll position to
  // that pane's band with no smooth-scroll, so it never animates through the panes in between.
  const goToTab = useCallback(
    (index) => {
      setActiveIndex(index);
      if (isMobile) return;
      const track = trackRef.current;
      if (!track) return;
      const trackTop = track.getBoundingClientRect().top + window.scrollY;
      const scrollable = Math.max(0, track.offsetHeight - window.innerHeight);
      const targetProgress = count > 1 ? (index + 0.5) / count : 0;
      const top = trackTop + targetProgress * scrollable;
      const root = document.documentElement;
      const prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto'; // defeat any smooth-scroll → true jump
      window.scrollTo(0, top);
      root.style.scrollBehavior = prevBehavior;
    },
    [isMobile, count]
  );

  // Keep the active tab in view inside the (horizontally scrollable) nav — nav scroll only,
  // never the page.
  useEffect(() => {
    const nav = tabsNavRef.current;
    const btn = nav?.children?.[activeIndex];
    if (!nav || !btn) return;
    const target = btn.offsetLeft - (nav.clientWidth - btn.clientWidth) / 2;
    nav.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [activeIndex]);

  // Show an edge fade only on the side(s) where more tabs are scrolled off-screen.
  useEffect(() => {
    const nav = tabsNavRef.current;
    if (!nav) return undefined;
    const update = () => {
      const max = nav.scrollWidth - nav.clientWidth;
      nav.classList.toggle(styles.canScrollLeft, nav.scrollLeft > 1);
      nav.classList.toggle(styles.canScrollRight, nav.scrollLeft < max - 1);
    };
    update();
    nav.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      nav.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [tabs]);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  const activeTab = tabs[activeIndex] || tabs[0];

  return (
    <section
      className={cn(styles.root, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      <div
        ref={trackRef}
        className={styles.track}
        style={isMobile ? undefined : { height: `${100 + count * SCROLL_VH_PER_TAB}vh` }}
      >
        <div className={styles.sticky}>
          <div className="container">
            <div className={styles.layout}>
              {(eyebrow || title || description) && (
                <header className={styles.header}>
                  {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
                  {title && (
                    <Heading tag="h2" size="xl" className={styles.title}>
                      {title}
                    </Heading>
                  )}
                  {description && (
                    <div
                      className={styles.description}
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}
                </header>
              )}

              <div
                ref={tabsNavRef}
                className={styles.tabsNav}
                role="tablist"
                aria-label="Section tabs"
              >
                {tabs.map((tab, index) => (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    id={`stackcards-tab-${index}`}
                    aria-selected={index === activeIndex}
                    aria-controls={`stackcards-pane-${index}`}
                    className={cn(styles.tab, { [styles.tabActive]: index === activeIndex })}
                    onClick={() => goToTab(index)}
                  >
                    {tab.tabName || tab.title}
                  </button>
                ))}
              </div>

              <div className={styles.stage}>
                {isMobile ? (
                  // Mobile: only the active pane is mounted, in normal flow (natural height,
                  // long content just scrolls), animated cleanly in on each switch.
                  <motion.div
                    key={activeIndex}
                    className={cn(styles.pane, styles.paneActive)}
                    role="tabpanel"
                    id={`stackcards-pane-${activeIndex}`}
                    aria-labelledby={`stackcards-tab-${activeIndex}`}
                    initial={{ opacity: 0, y: ENTER_OFFSET }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: ENTER_DURATION, ease: MOTION_EASE }}
                  >
                    <PaneContent tab={activeTab} imageNode={mediaFor(activeIndex)} />
                  </motion.div>
                ) : (
                  // Desktop: all panes mounted (images never reload). The active one fades +
                  // rises in; the outgoing snaps out instantly so there is no overlap/ghosting.
                  tabs.map((tab, index) => {
                    const isActive = index === activeIndex;
                    return (
                      <motion.div
                        key={index}
                        className={cn(styles.pane, { [styles.paneActive]: isActive })}
                        role="tabpanel"
                        id={`stackcards-pane-${index}`}
                        aria-labelledby={`stackcards-tab-${index}`}
                        aria-hidden={isActive ? undefined : true}
                        initial={false}
                        animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: ENTER_OFFSET }}
                        transition={{ duration: isActive ? ENTER_DURATION : 0, ease: MOTION_EASE }}
                        style={{
                          // Hidden panes are removed from the tab order + a11y tree, so a
                          // link inside an inactive pane's HTML can't be focused while it's
                          // aria-hidden (they're already opacity:0, so this is visually identical).
                          visibility: isActive ? 'visible' : 'hidden',
                          pointerEvents: isActive ? 'auto' : 'none',
                          zIndex: isActive ? 2 : 1,
                        }}
                      >
                        <PaneContent tab={tab} imageNode={mediaFor(index)} />
                      </motion.div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

PaneContent.propTypes = {
  tab: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    tagsTitle: PropTypes.string,
    tags: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }).isRequired,
  imageNode: PropTypes.node,
};

StackCards.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  // One Gatsby-optimized image per tab, in tab order (mapped positionally).
  children: PropTypes.node,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      tabName: PropTypes.string,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      tagsTitle: PropTypes.string,
      tags: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    })
  ),
};

export default StackCards;
